import React, { useEffect, useState } from 'react'
import {
  Image,
  ActivityIndicator,
  TextStyle,
  ViewStyle,
  TouchableOpacity,
  View,
} from 'react-native'
import { useHistory, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { useStores } from '@trivie/core'
import { Assessment } from '@trivie/core/src/models/Assessment'
import { Text } from '@trivie/ui-mobile/components/text'
import {
  Card,
  color,
  Header,
  iconAdaptive,
  iconArrowBack,
  SvgIcon,
  TopicHeader,
  TabController,
  iconTest,
  AnimatedGradientProgress,
  Medallion,
  iconNullCert,
  CertificationTooltip,
  iconTrophySmall,
  iconPath,
} from '@trivie/ui-mobile'
import { Box } from '@mui/material'
import { RecentPosts } from './recent-posts'
import { ReactComponent as PrePost } from './pre-post.svg'
import { LibraryQuizzesNull } from '../library-screen/tabs/library-quizzes-null'
import { LibraryResourcesNull } from '../library-screen/tabs/library-resources-null'
import { QuizMedalRow } from '@trivie/ui-mobile/components/quiz-medal-row'
import { ResourceRow } from '@trivie/ui-web/components/resource-row'
import { CountdownTimer } from '@trivie/ui-mobile/components/countdown-timer'
import { WorkflowApi } from '@trivie/core/src/services/api-objects/WorkflowApi'
import { Workflow } from '@trivie/core/src/models/workflow/Workflow'
import { TopicQuiz } from '@trivie/core/src/models/UserTopicDetails'
import { DateTime } from 'luxon'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'

const defaultTopicImage = require('./default-topic-image.png')

const useQuery = () => {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

export const TopicDetailScreen: React.FunctionComponent<any> = observer(() => {
  const {
    userStore,
    gameStore,
    badgeStore,
    quizStore,
    contestStore,
    libraryStore,
    resourceStore,
    talkStore,
  } = useStores()
  const { setQuiz } = gameStore
  const { selectedBadge, selectBadge, getBadgeDetail } = badgeStore
  const { setStartViewed, setEndViewed, markAsViewed } = resourceStore
  const { user } = userStore
  const { getResourcesForUser, selectedTopic, getAssessments } = libraryStore
  const { adaptive, getAdaptive } = quizStore
  const { refreshTopicPosts, sortedTopicPosts } = talkStore
  const { getContestActive, contestActive, getContestsForUser } = contestStore

  const [assessment, setAssessment] = useState<Instance<typeof Assessment | null | undefined>>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [viewingTextSource, setViewingTextSource] = useState<any>()

  const query = useQuery()

  const params = {
    topic_id: Number(query.get('topic_id')) || null,
    assignment_id: Number(query.get('assignment_id')) || null,
    subscription_user_id: Number(query.get('subscription_user_id')) || null,
    path_id: Number(query.get('path_id')) || null,
    content_user_id: Number(query.get('content_user_id')) || null,
  }

  // Navigation
  const history = useHistory()

  const goToQuiz = (quiz: Instance<typeof TopicQuiz>) => {
    setQuiz(undefined)
    if (quiz.subscription_user_id) {
      history.push(
        `/library/quiz/?subscription_user_id=${quiz.metadata.subscription_user_id}&topic_id=${quiz.metadata.topic_id}&quiz_id=${quiz.quiz_id}&path_id=${quiz.metadata.path_id}&content_user_id=${quiz.metadata.content_user_id}`,
      )
    } else {
      history.push(
        `/library/quiz/?assignment_id=${quiz.metadata.assignment_id}&topic_id=${quiz.metadata.topic_id}&quiz_id=${quiz.quiz_id}&path_id=${quiz.metadata.path_id}&content_user_id=${quiz.metadata.content_user_id}`,
      )
    }
  }

  const goToTest = () => {
    setQuiz(undefined)
    history.push(
      `/game/test/?assignment_id=${params.assignment_id}&topic_id=${params.topic_id}&quiz_id=${assessment?.assessment_quiz_id}&content_user_id=${params.content_user_id}`,
    )
  }
  const startKnowledgeBooster = () => {
    setQuiz(undefined)
    libraryStore.clearTopic()
    history.push(`/game/reinforcement/${params.topic_id}`)
  }

  const [workflow, setWorkflow] = useState<any>()
  const handleWorkflow = async (workflowID) => {
    const result = (await WorkflowApi.getWorkflow(workflowID)) as any
    const w = Workflow.create(result.data)
    setWorkflow(w)
    w.actions.map((action) => {
      if (action.action_type === 'award_badge') {
        getBadgeDetail(action.object_id!)
      }
    })
  }

  useEffect(() => {
    setActiveTab(0)
    selectBadge(undefined)
    ;(async () => {
      if (userStore.user) {
        getContestsForUser(userStore.user.id)
        getContestActive(params.topic_id!, 'topic')
        getResourcesForUser(userStore.user.id)
        onFocus()
        libraryStore.getTopicDetail(userStore.user!.id, params).then((data: any) => {
          if (data?.kind === 'not-found') {
            history.goBack()
          } else {
            if (data.workflow_id) {
              handleWorkflow(data.workflow_id)
            }

            getAssessments(userStore.user!.id).then((assessments) => {
              const target = assessments?.find(
                (test: { topic_id: number; assignment_id: number }) =>
                  test?.topic_id === Number(params.topic_id!) &&
                  test?.assignment_id === Number(params.assignment_id),
              )
              setAssessment(target)
              setLoading(false)
            })
          }
        })
        getAdaptive(userStore.user.id)
      }
      refreshTopicPosts(params.topic_id!)
    })()
  }, [])

  const onFocus = () => {
    const resourceData = localStorage.getItem('viewing-resource')
    if (resourceData) {
      const resource = JSON.parse(resourceData)
      if (resourceData) {
        localStorage.removeItem('viewing-resource')
        setEndViewed(DateTime.now().toUTC().toISO())
        markAsViewed(resource.resource_id, resource.metadata)
      }
    }
  }

  const viewResource = (resource: any) => {
    if (resource) {
      setStartViewed(DateTime.now().toUTC().toISO())
      resourceStore.getSourceDetails(resource.source_id).then((data) => {
        if (data) {
          if (data?.raw_text) {
            setViewingTextSource({ ...data, metadata: resource.metadata })
          } else if (data?.isVideo || data?.isAudio) {
            localStorage.setItem(
              'viewing-resource',
              JSON.stringify({ resource_id: data.resource_id, metadata: resource.metadata }),
            )
            history.push(
              `/library/source/?source_id=${resource.source_id}&assignment_id=${resource.metadata.assignment_id}&content_user_id=${resource.metadata.content_user_id}&path_id=${resource.metadata.path_id}&quiz_id=${resource.metadata.quiz_id}&topic_id=${resource.metadata.topic_id}`,
            )
          } else {
            localStorage.setItem(
              'viewing-resource',
              JSON.stringify({ resource_id: data.resource_id, metadata: resource.metadata }),
            )
            window.open(
              // @ts-ignore
              data.converted_pdf_url ? data.converted_pdf_url : data.filename_or_url,
              '_blank',
              'noopener',
            )
          }
        }
      })
    }
  }

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const renderTestIcon = () => {
    return (
      <View style={TEST_ICON_CONTAINER}>
        <View style={TEST_ICON}>
          <SvgIcon src={iconTest} size={42} />
        </View>
      </View>
    )
  }

  const renderTests = () => {
    if (selectedTopic?.pre_test && !selectedTopic?.pre_test.completed && assessment) {
      return (
        <Card
          onPress={goToTest}
          style={{
            ...BOOSTER,
            shadowRadius: 10,
            shadowOpacity: 0.2,
            shadowOffset: { height: 0, width: 0 },
          }}
        >
          {renderTestIcon()}
          <View style={{ flex: 1 }}>
            <Text style={{ color: color.blue }} variant="titleEmphasized" tx="Quiz.preTest" />
            <Text style={{ color: color.blue }} tx="Quiz.preTestTagline" variant="body2" />
          </View>
        </Card>
      )
    } else if (
      assessment &&
      selectedTopic?.quizzes_completed === selectedTopic?.quizzes?.length &&
      !selectedTopic?.post_test?.completed &&
      selectedTopic?.post_test?.quiz_id
    ) {
      return (
        <Card style={BOOSTER} onPress={goToTest}>
          {renderTestIcon()}
          <View style={{ flex: 1 }}>
            <Text style={{ color: color.blue }} variant="titleEmphasized" tx="Quiz.postTest" />
            <Text tx="Quiz.postTestTagline" style={{ color: color.blue }} variant="body2" />
          </View>
        </Card>
      )
    }
  }

  const renderKnowledgeBoosters = () => {
    return adaptive?.adaptive_topics.map((item: any) => {
      if (item.topic_id === Number(params.topic_id)) {
        return (
          <Card style={BOOSTER} onPress={() => startKnowledgeBooster()}>
            <View>
              <Text
                style={{ color: 'rgb(206,0,253)', marginBottom: 4 }}
                variant="titleEmphasized"
                tx="Quiz.booster"
              />
              <Text
                variant="body2"
                tx="Quiz.reinforcementSubtitle"
                style={{ color: 'rgb(206,0,253)', fontWeight: '400' }}
              />
            </View>
            <SvgIcon viewBox="6 0 46 46" size={42} src={iconAdaptive} />
          </Card>
        )
      }
    })
  }

  const renderTabContent = () => {
    if (activeTab === 1) {
      if (!selectedTopic?.resources?.length) {
        return (
          <div style={TAB}>
            <LibraryResourcesNull style={{ paddingTop: 20 }} />
          </div>
        )
      }

      return (
        <div>
          {selectedTopic?.resources
            ?.slice()
            ?.sort((a, b) => a.title.localeCompare(b.title))
            .map((resource) => {
              return (
                <ResourceRow
                  resource={resource}
                  style={{ marginBottom: 16 }}
                  onPress={() => viewResource(resource)}
                />
              )
            })}
        </div>
      )
    } else if (activeTab === 0) {
      if (!selectedTopic?.topic_id) {
        return null
      }

      if (!selectedTopic?.quizzes?.length) {
        return (
          <div style={TAB}>
            <LibraryQuizzesNull style={{ paddingTop: 20 }} />
          </div>
        )
      }

      return (
        <div>
          {renderTests()}
          {renderKnowledgeBoosters()}
          {selectedTopic?.quizzes
            .slice()
            .sort((a, b) => {
              const o1 = selectedTopic?.content_order?.find((o) => o.object_id === a.quiz_id)
              const o2 = selectedTopic?.content_order?.find((o) => o.object_id === b.quiz_id)
              if (o1 && o2) {
                return Number(Number(o1.order) - Number(o2.order))
              }
              return 0
            })
            .map((q) => {
              return (
                <QuizMedalRow
                  topic={selectedTopic}
                  locked={q.is_locked || q.unlocks_after}
                  lockedByPretest={selectedTopic?.pre_test && !selectedTopic?.pre_test.completed}
                  key={`${q.quiz_id}`}
                  backgroundColor={q.pack_color}
                  quiz={q}
                  due={q.assignment_end_date}
                  maxAttempts={selectedTopic?.max_attempts ?? 0}
                  imgSource={q.topic_image}
                  isReinforcement={false}
                  onPress={() => goToQuiz(q)}
                  style={{ marginBottom: 12 }}
                />
              )
            })}
        </div>
      )
    } else if (activeTab === 2) {
      return (
        <>
          <RecentPosts visible topicID={params.topic_id!} assignmentID={params.assignment_id!} />
          {sortedTopicPosts && sortedTopicPosts.length === 0 && (
            <Text
              variant="body1Medium"
              style={{ color: color.shade50, textAlign: 'center' }}
              tx="Topic.nullPosts"
            />
          )}
        </>
      )
    }
  }

  const [showTooltip, setShowTooltip] = useState(false)

  const certification =
    selectedTopic?.has_certification &&
    selectedTopic?.certification_criteria !== null &&
    workflow &&
    workflow.levels !== null
      ? {
          info: selectedTopic?.certification_criteria,
          certification_level: selectedTopic?.certification_level,
        }
      : null

  const anchorRef = React.useRef<any>(null)

  const tabs = [
    {
      name: 'quizzes',
      label: 'Library.quizzes',
      component: renderTabContent(),
      onPress: () => setActiveTab(0),
      numeral: String(selectedTopic?.quizzes?.length ?? 0),
    },
    {
      name: 'resources',
      label: 'Library.resources',
      component: renderTabContent(),
      onPress: () => setActiveTab(1),
      numeral: String(selectedTopic?.resources?.length ?? 0),
      enable:
        selectedTopic?.resources?.length &&
        (!selectedTopic?.pre_test ||
          (selectedTopic?.pre_test && selectedTopic?.pre_test.completed)),
    },
    {
      name: 'posts',
      label: 'Library.posts',
      component: renderTabContent(),
      onPress: () => setActiveTab(2),
      numeral: String(sortedTopicPosts?.length ?? 0),
      enable: userStore.user?.workspace_settings?.feed_enabled,
    },
  ]

  const renderTimer = () => {
    if (
      selectedTopic?.quizzes_completed !== selectedTopic?.quizzes.length &&
      !userStore.isSubscriptionWorkspace
    ) {
      if (selectedTopic?.due_date) {
        return <CountdownTimer locale={user?.language ?? 'en'} date={selectedTopic.due_date} />
      }
      if (selectedTopic?.end_date && !selectedTopic?.due_date) {
        return <CountdownTimer locale={user?.language ?? 'en'} date={selectedTopic.end_date} />
      }
    }
  }

  return (
    <div style={CONTAINER}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <div style={HEADER}>
            <Header leftIcon={iconArrowBack} onLeftPress={history.goBack} center />
          </div>
        </div>
      </div>
      {loading ? (
        <ActivityIndicator
          color="#999"
          style={{ flex: 1, height: window.innerHeight - 100 }}
          size="small"
        />
      ) : (
        <>
          <div style={CONTENT}>
            <div
              style={{ position: 'relative', height: 141, borderRadius: 16, overflow: 'hidden' }}
            >
              <img
                src={selectedTopic?.topic_image || defaultTopicImage}
                style={{ height: 141, width: '100%', objectFit: 'cover', position: 'absolute' }}
              />
              <div style={DUE_CONTAINER}>
                {contestActive && (
                  <TouchableOpacity
                    onPress={() => history.push(`/library`)}
                    style={{
                      borderRadius: 100,
                      width: 32,
                      height: 32,
                      borderWidth: 2,
                      borderColor: color.tint70,
                      marginRight: params.path_id
                        ? 16
                        : selectedTopic?.due_date &&
                            selectedTopic?.quizzes_completed !== selectedTopic?.quizzes.length
                          ? 16
                          : 0,
                      alignItems: 'center',
                      backgroundColor: color.shade70,
                    }}
                  >
                    <View style={{ top: 6.5, left: 0.35 }}>
                      <SvgIcon size={15} src={iconTrophySmall} color={color.white} />
                    </View>
                  </TouchableOpacity>
                )}
                {params.path_id ? (
                  <TouchableOpacity
                    onPress={() =>
                      history.push(
                        `/library/path/?assignment_id=${params.assignment_id}&path_id=${params.path_id}`,
                      )
                    }
                    style={{
                      borderRadius: 100,
                      width: 32,
                      height: 32,
                      borderWidth: 2,
                      borderColor: color.tint70,
                      marginRight:
                        selectedTopic?.due_date &&
                        selectedTopic?.quizzes_completed !== selectedTopic?.quizzes.length
                          ? 16
                          : 0,
                      alignItems: 'center',
                      backgroundColor: color.shade70,
                    }}
                  >
                    <View style={{ top: 6.5, left: 0.35 }}>
                      <SvgIcon size={15} src={iconPath} color={color.white} />
                    </View>
                  </TouchableOpacity>
                ) : null}
                {renderTimer()}
              </div>
            </div>

            <View style={{ backgroundColor: '#fff' }}>
              <Text
                variant="headline"
                text={selectedTopic?.topic_title}
                align="center"
                style={{
                  marginTop: 20,
                  marginBottom: 12,
                  paddingLeft: 5,
                  paddingRight: 5,
                }}
              />
              {selectedTopic?.quizzes_completed !== selectedTopic?.quizzes.length && (
                <Text
                  align="center"
                  variant="titleEmphasized"
                  tx={
                    selectedTopic?.pre_test && !selectedTopic?.pre_test.completed
                      ? 'Topic.hasPreTest'
                      : 'Topic.quizzesComplete'
                  }
                  txOptions={{
                    completed: selectedTopic?.quizzes_completed ?? 0,
                    total: selectedTopic?.quizzes.length ?? 0,
                  }}
                  style={{ marginBottom: 10, color: color.shade50 }}
                />
              )}
            </View>
            {selectedTopic?.quizzes_completed &&
            selectedTopic?.quizzes_completed === selectedTopic?.quizzes.length ? (
              <>
                <TopicHeader
                  disabled={
                    !adaptive?.adaptive_topics ||
                    adaptive?.adaptive_topics.length === 0 ||
                    !selectedTopic?.topic_id
                  }
                  showCert={certification}
                  badge={workflow ? selectedBadge : null}
                  boost={adaptive?.adaptive_topics && adaptive?.adaptive_topics.length > 0}
                  certification={selectedTopic?.certification_level}
                  topicComplete={selectedTopic?.quizzes_completed === selectedTopic?.quizzes.length}
                  hasCertification={selectedTopic?.has_certification}
                  backgroundImage={selectedTopic?.topic_image}
                  recall={selectedTopic?.knowledge_pct}
                />
                <View style={SCORE_CONTAINER}>
                  {selectedTopic?.pre_test && (
                    <View style={{ flexDirection: 'column' }}>
                      <Text
                        style={{ marginBottom: 4 }}
                        variant="titleEmphasized"
                        tx="Quiz.preTest"
                      />
                      {selectedTopic?.pre_test && selectedTopic?.pre_test.completed && (
                        <Text
                          style={TEST_SCORE}
                          variant="body1Emphasized"
                          text={`${selectedTopic?.pre_test.score}%`}
                        />
                      )}
                    </View>
                  )}
                  {selectedTopic?.post_test && (
                    <View style={{ flexDirection: 'column' }}>
                      <Text style={LABEL} variant="body1" tx="Quiz.postTest" />
                      {selectedTopic?.post_test.completed && (
                        <Text
                          style={POST_TEST_SCORE}
                          variant="body1Emphasized"
                          text={`${selectedTopic?.post_test.score}%`}
                        />
                      )}
                    </View>
                  )}
                </View>
              </>
            ) : (
              <>
                <>
                  <AnimatedGradientProgress
                    style={{
                      ...GRADIENT,
                      marginBottom: selectedTopic?.post_test || selectedTopic?.pre_test ? 12 : 0,
                    }}
                    leftIcon={selectedTopic?.pre_test ? <PrePost /> : null}
                    rightIcon={
                      workflow && selectedBadge && certification ? (
                        <View ref={anchorRef} style={{ top: -2, position: 'relative' }}>
                          <View style={{ zIndex: 9999, position: 'relative' }}>
                            <SvgIcon src={iconNullCert} size={32} />
                          </View>
                          <View ref={anchorRef} style={{ top: -31, left: -32 }}>
                            {selectedBadge?.icon_url && (
                              <Image
                                style={{
                                  width: 31,
                                  height: 31,
                                  borderRadius: 100,
                                  opacity: 0.7,
                                  left: 20,
                                }}
                                source={{ uri: selectedBadge?.icon_url }}
                              />
                            )}
                          </View>
                        </View>
                      ) : selectedBadge ? (
                        <View ref={anchorRef} style={{ top: 0, left: -21 }}>
                          {selectedBadge?.icon_url && (
                            <Image
                              style={{
                                width: 31,
                                height: 31,
                                borderRadius: 100,
                                opacity: 0.7,
                                left: 20,
                              }}
                              source={{ uri: selectedBadge?.icon_url }}
                            />
                          )}
                        </View>
                      ) : selectedTopic?.has_certification &&
                        selectedTopic?.certification_level === null ? (
                        <View ref={anchorRef} style={{ top: -2 }}>
                          <SvgIcon src={iconNullCert} size={32} />
                        </View>
                      ) : selectedTopic?.has_certification ? (
                        <View style={{ top: -36, right: -36 }} ref={anchorRef}>
                          <Medallion scale={0.32} type={'Not-Started'} />
                        </View>
                      ) : selectedTopic?.post_test && selectedTopic?.post_test.completed ? (
                        <PrePost />
                      ) : null
                    }
                    rightPress={() => setShowTooltip(true)}
                    value={
                      (selectedTopic?.quizzes_completed ?? 0) / (selectedTopic?.quizzes.length ?? 1)
                    }
                  />
                  <View
                    style={{ ...SCORE_CONTAINER, marginBottom: selectedTopic?.pre_test ? 20 : 35 }}
                  >
                    {selectedTopic?.pre_test && (
                      <View style={{ flexDirection: 'column' }}>
                        <Text style={LABEL} variant="body1" tx="Quiz.preTest" />
                        {selectedTopic?.pre_test && selectedTopic?.pre_test.completed && (
                          <Text
                            style={TEST_SCORE}
                            variant="body1Emphasized"
                            text={`${selectedTopic?.pre_test.score}%`}
                          />
                        )}
                      </View>
                    )}
                    {selectedTopic?.post_test && (
                      <View style={{ flexDirection: 'column', position: 'absolute', right: 0 }}>
                        <Text style={LABEL} variant="body1" tx="Quiz.postTest" />
                        {selectedTopic?.post_test.completed && (
                          <Text
                            style={POST_TEST_SCORE}
                            variant="body1Emphasized"
                            text={`${selectedTopic?.post_test.score}%`}
                          />
                        )}
                      </View>
                    )}
                  </View>
                </>
                <CertificationTooltip
                  badge={selectedBadge}
                  criteria={certification?.info}
                  anchorRef={anchorRef}
                  handleClose={(event: any) => {
                    if (
                      anchorRef.current &&
                      anchorRef.current.contains(event.target as HTMLElement)
                    ) {
                      return
                    }
                    setShowTooltip(false)
                  }}
                  visible={showTooltip}
                />
              </>
            )}

            <TabController
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              tabs={tabs}
              containerStyle={{
                marginBottom: 20,
                justifyContent: 'flex-start',
                paddingHorizontal: 0,
              }}
            />
          </div>
        </>
      )}
      <Dialog
        maxWidth="sm"
        align="left"
        onClose={() => {
          setViewingTextSource(null)
          setEndViewed(DateTime.now().toUTC().toISO())
          markAsViewed(viewingTextSource?.resource_id, viewingTextSource?.metadata)
        }}
        title="Preview"
        open={Boolean(viewingTextSource)}
      >
        <Box flexDirection="column" display="flex" p={2} sx={{ whiteSpace: 'pre-wrap' }}>
          <Text style={{ marginBottom: 16 }} variant="headline" text={viewingTextSource?.title} />
          {viewingTextSource?.raw_text}
        </Box>
      </Dialog>
    </div>
  )
})

/**
 * Styling
 */
const CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
}
const CONTENT: React.CSSProperties = {
  flexDirection: 'column',
  justifyContent: 'center',
  display: 'flex',
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER: React.CSSProperties = { width: '100%' }
const BOOSTER: ViewStyle = {
  height: 84,
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: 12,
}
const TEST_ICON_CONTAINER: ViewStyle = {
  marginRight: 29,
  alignSelf: 'center',
  width: 42,
  flexDirection: 'column',
  justifyContent: 'center',
  height: 42,
}
const TEST_ICON: ViewStyle = { flex: 1, justifyContent: 'center', flexDirection: 'row' }
const DUE_CONTAINER: React.CSSProperties = {
  padding: 3,
  borderRadius: 16,
  alignSelf: 'flex-end',
  position: 'absolute',
  right: 18,
  bottom: 14,
  flexDirection: 'row',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
}
const TAB: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
}
const SCORE_CONTAINER: ViewStyle = {
  marginBottom: 20,
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
}
const LABEL: TextStyle = { fontSize: 12, color: color.shade70 }
const TEST_SCORE: TextStyle = { fontWeight: '700', fontSize: 12, color: color.shade70 }
const POST_TEST_SCORE: TextStyle = { ...TEST_SCORE, textAlign: 'right' }
const GRADIENT: ViewStyle = { alignSelf: 'center', width: '100%' }
